/**
 * Copyright ToolBelt Data Inc., 2020 All Rights Reserved
 */

import React, {useEffect, useMemo, useState} from 'react';
import { Row, Col, Dropdown, OverlayTrigger, Tooltip } from 'react-bootstrap';
import styled from 'styled-components';
import { useSelector, useDispatch } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';

import {setExploreByContact, switchUniverse} from '../universeSlice';
import BusinessUniverseFilter from './BusinessUniverseFilter';
import ContractorUniverseFilter from "./ContractorUniverseFilter";
import FormTextInput from "../../../common/components/FormTextInput";
import { getProductConfig } from "./utils/ProductConfigHelper";
import FilterCheckbox from "./components/form/FilterCheckbox";
import {APP_COLORS} from "../../../common/Constants";
import tokens from '../../../common/styles/design-tokens';

const UniverseDropdown = styled(Dropdown)`
    margin: 0 !important;
`;

const TitleRow = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    padding-left: 1px;
    width: 100%;
`;

const SearchFieldWrap = styled.div`
    position: relative;

    .form-control {
        border-radius: 8px;
        padding-right: ${(p) => (p.$clearable ? '3.25rem' : '2.25rem')} !important;
    }
`;

const SearchMagnifier = styled.span`
    position: absolute;
    right: ${(p) => (p.$clearable ? '2.25rem' : '10px')};
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
    color: ${tokens.colors['gray-500']};
    font-size: 14px;
`;

const FilterSidebar = () => {
    const dispatch = useDispatch();

    const universeState = useSelector(state => state.universe);
    const accountData = useSelector(state => state.account.account);

    const currentUniverse = universeState.currentUniverse;
    const currentUniverseData = universeState.universes[currentUniverse];

    const productConfig = useMemo(() => {
        return getProductConfig(accountData);
    }, [accountData]);

    const exploreByContact = currentUniverseData?.exploreByContact ?? false;

    const [searchText, setSearchText] = useState('');
    const [showOptions, setShowOptions] = useState(false);

    const availableProducts = useMemo(() => {
        const list = [];

        if (!productConfig) return [];

        if (productConfig.business_universe) {
            list.push({ value: 'BUSINESS', label: 'Business Universe' });
        }

        if (productConfig.contractor_universe) {
            list.push({ value: 'CONTRACTOR', label: 'Contractor Universe' });
        }

        return list;
    }, [productConfig]);

    const activeProduct = availableProducts.find(p => p.value === currentUniverse) || availableProducts[0];

    const headerTitle =
        currentUniverse === 'CONTRACTOR' && activeProduct?.label
            ? activeProduct.label.toUpperCase()
            : activeProduct?.label;

    const isContractorWireframe = currentUniverse === 'CONTRACTOR';

    useEffect(() => {
        if (availableProducts.length > 0) {
            const isCurrentValid = availableProducts.find(u => u.value === currentUniverse);

            if (!isCurrentValid) {
                console.log(`[FilterSidebar] Auto-switching universe to ${availableProducts[0].value}`);
                dispatch(switchUniverse(availableProducts[0].value));
            }
        }
    }, [availableProducts, currentUniverse, dispatch]);

    const handleSwitchUniverse = (eventKey) => {
        if (eventKey !== currentUniverse) {
            dispatch(switchUniverse(eventKey));
            setSearchText('');
        }
    };

    const handleSetExploreByContact = (value) => {
        dispatch(setExploreByContact(value));
    }

    const renderContent = () => {
        if (!activeProduct) return <div style={{padding: 20}}>Loading configuration...</div>;

        switch (currentUniverse) {
            case 'BUSINESS':
                return productConfig.business_universe ?
                    <BusinessUniverseFilter searchText={searchText} /> : null;
            case 'CONTRACTOR':
                return productConfig.contractor_universe ?
                    <ContractorUniverseFilter searchText={searchText} /> : null;
            default:
                return <div>Select a universe</div>;
        }
    };

    if (!productConfig || availableProducts.length === 0) return null;

    return (
        <div>
            <Row>
                <Col xl={12} lg={12} md={12} sm={12}>
                    <div className="search-filter-container">

                        {/* --- Header & Dropdown --- */}
                        <div style={{ padding: `${tokens.spacing.sm} ${tokens.spacing.sm} ${tokens.spacing.xs} ${tokens.spacing.sm}` }}>
                            <TitleRow className="filters-search">
                                {availableProducts.length > 1 ? (
                                    <UniverseDropdown onSelect={handleSwitchUniverse}>
                                        <Dropdown.Toggle
                                            as="button"
                                            id="product-dropdown"
                                            style={{
                                                textDecoration: 'none',
                                                color: APP_COLORS['tech-blue'],
                                                padding: '0',
                                                border: 'none',
                                                backgroundColor: 'transparent',
                                                fontWeight: tokens.typography.fontWeight.bold,
                                                cursor: 'pointer',
                                                fontSize: tokens.typography.fontSize.lg,
                                                textAlign: 'left',
                                            }}
                                        >
                                            {headerTitle}
                                        </Dropdown.Toggle>

                                        <Dropdown.Menu>
                                            {availableProducts.map((product) => (
                                                <Dropdown.Item
                                                    key={product.value}
                                                    eventKey={product.value}
                                                    active={currentUniverse === product.value}
                                                >
                                                    {product.label}
                                                </Dropdown.Item>
                                            ))}
                                        </Dropdown.Menu>
                                    </UniverseDropdown>
                                ) : (
                                    <span style={{
                                        color: APP_COLORS['tech-blue'],
                                        fontWeight: tokens.typography.fontWeight.bold,
                                        fontSize: tokens.typography.fontSize.lg
                                    }}>
                                        {headerTitle}
                                    </span>
                                )}

                                <OverlayTrigger
                                    placement="right"
                                    overlay={
                                        <Tooltip>
                                            {activeProduct?.value === 'CONTRACTOR'
                                                ? 'The ToolBeltData Contractor Universe has over 5 million profiles of contractors and skilled trade professionals.'
                                                : 'The ToolBeltData Business Universe provides comprehensive business data and insights.'}
                                        </Tooltip>
                                    }
                                >
                                    <span style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backgroundColor: APP_COLORS['tech-blue'],
                                        color: 'white',
                                        width: '14px',
                                        height: '14px',
                                        borderRadius: '50%',
                                        fontSize: '10px',
                                        fontWeight: tokens.typography.fontWeight.bold,
                                        fontStyle: 'italic',
                                        fontFamily: 'serif',
                                        cursor: 'pointer',
                                        flexShrink: 0,
                                        marginLeft: 'auto',
                                    }}>ℹ</span>
                                </OverlayTrigger>

                            </TitleRow>

                            <div style={{ paddingLeft: '15px', marginTop: tokens.spacing.xs, marginBottom: tokens.spacing.xs, borderTop: `2px solid ${APP_COLORS['tech-blue']}`, paddingTop: tokens.spacing.xs }}>
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'flex-end',
                                        cursor: 'pointer',
                                        fontSize: tokens.typography.fontSize.xs,
                                        fontWeight: tokens.typography.fontWeight.semibold,
                                        letterSpacing: '0.05em',
                                        color: APP_COLORS['tech-blue'],
                                        userSelect: 'none',
                                    }}
                                    onClick={() => setShowOptions(!showOptions)}
                                >
                                    {showOptions ? 'HIDE OPTIONS' : 'OPTIONS'}
                                </div>

                                {showOptions && (
                                    <div style={{ marginTop: tokens.spacing.xs }}>
                                        <FilterCheckbox
                                            ftOption={{
                                                id: 'explore-by-contact',
                                                label: 'Explore by Contact',
                                                insight: {
                                                    title: 'INSIGHT - How it Works',
                                                    text: 'Selecting this option will change the core dataset used in resolving queries to our contact dataset. Results will include all contacts for a company however we will drop companies that do not have a contact associated to them. Select the tool tip to learn more about the difference between companies and contacts.'
                                                },
                                            }}
                                            stateObj={{ 'explore-by-contact': exploreByContact }}
                                            handleSubmitFilter={() => handleSetExploreByContact(true)}
                                            handleRemoveFilter={() => handleSetExploreByContact(false)}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* --- Common Search Input --- */}
                        <div style={{ padding: `0 15px ${tokens.spacing.xs} 15px` }}>
                            {isContractorWireframe ? (
                                <SearchFieldWrap $clearable={searchText !== ''}>
                                    <FormTextInput
                                        value={searchText}
                                        onChange={setSearchText}
                                        placeholder="Filter Search..."
                                        clearable={searchText !== ''}
                                        clearableHandle={() => setSearchText('')}
                                        inputStyle={{
                                            fontSize: tokens.typography.fontSize.xs,
                                            borderRadius: '8px',
                                        }}
                                    />
                                    <SearchMagnifier $clearable={searchText !== ''} aria-hidden>
                                        <FontAwesomeIcon icon={faMagnifyingGlass} />
                                    </SearchMagnifier>
                                </SearchFieldWrap>
                            ) : (
                                <FormTextInput
                                    value={searchText}
                                    onChange={setSearchText}
                                    placeholder="Filter Search..."
                                    clearable={searchText !== ''}
                                    clearableHandle={() => setSearchText('')}
                                    inputStyle={{ fontSize: tokens.typography.fontSize.xs }}
                                />
                            )}
                        </div>

                        {/* --- Dynamic Content Area --- */}
                        <div className="filters-content">
                            {renderContent()}
                        </div>
                    </div>
                </Col>
            </Row>
        </div>
    );
};

export default FilterSidebar;